import { Meta } from '@components/atoms/MetaTags/Meta';
import { Oewa } from '@components/atoms/Oewa/Oewa';
import { NewsletterOverview } from '@components/organisms/Newsletter/NewsletterOverview';
import { defaultNewsletterConsent } from '@components/organisms/Newsletter/newsletterContent';
import {
  GetBySlugFullDocument,
  GetTvmediaNewsletterOverviewDocument,
  type GetTvmediaNewsletterOverviewQuery,
} from '@lib/graphql/generated';
import {
  type NewsletterCardContentQuery,
  type NewsletterSignupContentQuery,
  newsletterCardContentQuery,
  newsletterSignupContentQuery,
} from '@lib/graphql/newsletterSignupContent';
import { createUrqlClient } from '@lib/graphql/urql';
import { Image } from '@vgn-medien-holding/vgn-fe-components';
import Head from 'next/head';

export function NewsletterPage({ data }) {
  const { overview, metadata, oewa, consentContent } = data;

  return (
    <>
      <Oewa
        pageKey={oewa?.page_key || 'Service'}
        categoryKey={oewa?.category_key || 'Sonstiges'}
        profileKey={oewa?.profile_key || 'Sonstiges'}
      />
      {metadata && <Head>{Meta(metadata)}</Head>}
      <div className="relative isolate overflow-hidden pt-24">
        {/* Keep the image size independent of conditional consent and success content. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-svh opacity-75" aria-hidden="true">
          <Image copyright="" src="/assets/tvm_assets/bg-waves.png" sizes="100vw" alt="" fill imageMode="cover" />
          <div className="absolute bottom-0 left-0 h-1/2 w-full bg-gradient-to-t from-black" />
        </div>
        <div className="relative pt-10 pb-16">
          <NewsletterOverview overview={overview} consentContent={consentContent} />
        </div>
      </div>
    </>
  );
}

// The signup follows the published overview. Settings list every published
// TV-MEDIA newsletter, so existing subscriptions stay manageable regardless of it.
export const newsletterSsrLogic = async ({ allNewsletters = false } = {}) => {
  const { ssrQuery } = createUrqlClient();

  const [{ data: pageData }, { data: overviewData, error }] = await Promise.all([
    ssrQuery({
      query: GetBySlugFullDocument,
      variables: { portal: 'tvmedia', slug: 'newsletter' },
    }),
    ssrQuery({
      query: GetTvmediaNewsletterOverviewDocument,
      variables: {},
    }),
  ]);

  const page = pageData?.findBySlug;
  // Avoid caching an empty form when the CMS is temporarily unavailable.
  if (error) throw new Error('Could not load TV-MEDIA newsletters');
  // Only a published overview may expose newsletters. While it is a draft the
  // API omits it, and the signup shows its unavailable state instead.
  const overview: GetTvmediaNewsletterOverviewQuery['newsletter_overviews'][number] =
    overviewData?.newsletter_overviews?.find(Boolean) ?? { title: null, subheadline: null, newsletters: [] };
  if (allNewsletters) {
    // Keep the overview order for its entries and append the remaining ones.
    const published = overviewData?.newsletters?.filter(Boolean) ?? [];
    const ordered = overview.newsletters.filter((entry) => published.some(({ id }) => id === entry?.id));
    overview.newsletters = [...ordered, ...published.filter(({ id }) => !ordered.some((entry) => entry.id === id))];
  }
  let consentContent = defaultNewsletterConsent;
  const cardFields = overviewData?.newsletterContentType?.fields?.map((field) => field.name) ?? [];
  if (['sending_frequency', 'preview_url'].every((field) => cardFields.includes(field))) {
    const cards = await ssrQuery<NewsletterCardContentQuery, Record<string, never>>({
      query: newsletterCardContentQuery,
      variables: {},
    });
    if (cards.error) throw new Error('Could not load TV-MEDIA newsletter card content');
    overview.newsletters = overview.newsletters?.map((newsletter) => ({
      ...newsletter,
      ...cards.data?.newsletters?.find((entry) => entry.id === newsletter.id),
    }));
  }
  // Allow the frontend and CMS releases in either order. Request editable copy
  // only once the live schema exposes all fields; old APIs use the supplied copy.
  const contentFields = [
    'newsletter_consent_summary',
    'newsletter_consent_text',
    'marketing_consent_summary',
    'marketing_consent_text',
    'privacy_text',
  ];
  const availableFields = overviewData?.signupContentType?.fields?.map((field) => field.name) ?? [];
  if (contentFields.every((field) => availableFields.includes(field))) {
    const result = await ssrQuery<NewsletterSignupContentQuery, Record<string, never>>({
      query: newsletterSignupContentQuery,
      variables: {},
    });
    if (result.error) throw new Error('Could not load TV-MEDIA newsletter consent copy');
    const copy = result.data?.newsletter_overviews?.find(Boolean);
    consentContent = {
      newsletter: {
        summary: copy?.newsletter_consent_summary || defaultNewsletterConsent.newsletter.summary,
        text: copy?.newsletter_consent_text || defaultNewsletterConsent.newsletter.text,
      },
      marketing: {
        summary: copy?.marketing_consent_summary || defaultNewsletterConsent.marketing.summary,
        text: copy?.marketing_consent_text || defaultNewsletterConsent.marketing.text,
      },
      privacy: copy?.privacy_text || defaultNewsletterConsent.privacy,
    };
  }

  return {
    revalidate: 60,
    props: {
      data: {
        metadata: page && 'metadata' in page ? page.metadata : null,
        oewa: page && 'oewa' in page ? page.oewa : null,
        overview,
        consentContent,
      },
      slug: 'newsletter',
    },
  };
};

// Newsletter availability must not prevent users from accessing their existing
// profile and password settings, including during the first CMS rollout.
export const newsletterSettingsSsrLogic = async () => {
  try {
    return await newsletterSsrLogic({ allNewsletters: true });
  } catch {
    return { revalidate: 60, props: { data: null } };
  }
};
